import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Settings, 
  Sparkles,
  MessageSquare,
  Star,
  Moon,
  Sun,
  Bell,
  Mail,
  Calendar,
  List
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  resourcesData, 
  useUserCloud, 
  EmailCaptureModal,
  PromptBlock, 
  EmergentCTA,
  AboutEmergent,
  RelatedResources, 
  AuthorCard,
  resourceColors as colors 
} from '../ResourceComponents';

const resource = resourcesData['add-on-features'];

// Feature preview mockup
const FeaturePreview = ({ type }) => {
  const [isDark, setIsDark] = useState(false);

  if (type === 'dark-mode') {
    return (
      <div className={`rounded-xl p-4 border transition-colors ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-3">
          <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Preview</span>
          <button 
            onClick={() => setIsDark(!isDark)}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}
          >
            {isDark ? <Sun className="w-4 h-4 text-yellow-400" /> : <Moon className="w-4 h-4 text-gray-600" />}
          </button>
        </div>
        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Click the toggle to switch between light and dark mode.
        </p>
      </div>
    );
  }

  if (type === 'announcement') {
    return (
      <div className="rounded-xl overflow-hidden border border-gray-200">
        <div className="bg-gradient-to-r from-purple-600 to-pink-500 px-4 py-2 flex items-center justify-between">
          <p className="text-sm text-white font-medium">🎉 New: Check out our latest features!</p>
          <button className="text-white/80 hover:text-white text-xl leading-none">&times;</button>
        </div>
        <div className="bg-gray-50 p-4 text-xs text-gray-400 text-center">
          Your website content here...
        </div>
      </div>
    );
  }

  if (type === 'contact') {
    return (
      <div className="rounded-xl border border-gray-200 p-4 bg-white">
        <h4 className="font-medium text-gray-900 mb-3">Get in touch</h4>
        <div className="space-y-2">
          <input 
            type="text" 
            placeholder="Your name" 
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-gray-50"
            readOnly
          />
          <input 
            type="email" 
            placeholder="your@email.com" 
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-gray-50"
            readOnly
          />
          <textarea 
            placeholder="Your message..." 
            rows={2}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-gray-50 resize-none"
            readOnly
          />
          <button className="w-full py-2 rounded-lg bg-teal-500 text-white text-sm font-medium">
            Send Message
          </button>
        </div>
      </div>
    );
  }

  if (type === 'testimonials') {
    return (
      <div className="rounded-xl border border-gray-200 p-4 bg-white">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex-shrink-0" />
          <div className="flex-1">
            <div className="flex items-center gap-1 mb-1">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-sm text-gray-600 italic mb-2">"Amazing work! Highly recommend."</p>
            <p className="text-xs font-medium text-gray-900">Sarah J.</p>
            <p className="text-xs text-gray-500">CEO, StartupXYZ</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Feature card
const FeatureCard = ({ icon: Icon, title, description, color, previewType }) => (
  <div className="p-5 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
    <div className="flex items-start gap-4">
      <div 
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}15` }}
      >
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="flex-1">
        <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
        <p className="text-sm text-gray-600 mb-4">{description}</p>
        {previewType && <FeaturePreview type={previewType} />}
      </div>
    </div>
  </div>
);

const AddOnFeaturesPage = () => {
  const { userEmail, saveUser, saveResource } = useUserCloud();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (userEmail) {
      setHasAccess(true);
      saveResource(resource.id);
    }
  }, [userEmail]);

  const handleSaveToCloud = async (email) => {
    saveUser(email);
    saveResource(resource.id);
    setShowEmailModal(false);
    setHasAccess(true);
  };

  if (!hasAccess && !userEmail) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-lg w-full text-center">
          <div 
            className="w-24 h-24 rounded-3xl mx-auto mb-6 flex items-center justify-center"
            style={{ background: `${resource.color}20` }}
          >
            <Settings className="w-12 h-12" style={{ color: resource.color }} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{resource.title}</h1>
          <p className="text-gray-600 mb-8">{resource.description}</p>
          
          <button
            onClick={() => setShowEmailModal(true)}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-medium text-white transition-all hover:opacity-90"
            style={{ background: colors.teal }}
          >
            <Sparkles className="w-5 h-5" />
            Save to Your Cloud
          </button>
          
          <p className="text-gray-400 text-sm mt-4">
            Enter your email to access this resource
          </p>

          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mt-8 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Data Dreaming
          </Link>
        </div>

        <EmailCaptureModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          onSubmit={handleSaveToCloud}
          resourceTitle={resource.title}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Resource by</span>
            <span className="text-sm font-medium text-gray-900">{resource.author.handle}</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div 
        className="py-16 px-4"
        style={{ background: `linear-gradient(180deg, ${resource.color}10 0%, transparent 100%)` }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <span 
              className="px-3 py-1 rounded-full text-xs font-medium"
              style={{ background: `${resource.color}20`, color: resource.color }}
            >
              {resource.platform} Prompt
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{resource.title}</h1>
          <p className="text-xl text-gray-600 max-w-2xl">{resource.description}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        
        {/* Feature previews */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">What You Can Add</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <FeatureCard 
              icon={MessageSquare}
              title="Contact Form"
              description="Let visitors reach out directly from your site."
              color="#00CED1"
              previewType="contact"
            />
            <FeatureCard 
              icon={Star}
              title="Testimonials"
              description="Show social proof with client reviews."
              color="#FFD700"
              previewType="testimonials"
            />
            <FeatureCard 
              icon={Moon}
              title="Dark/Light Mode"
              description="Give visitors a theme preference toggle."
              color="#6A0DAD"
              previewType="dark-mode"
            />
            <FeatureCard 
              icon={Bell}
              title="Announcement Banner"
              description="Highlight news, promos, or updates."
              color="#FF007F"
              previewType="announcement"
            />
          </div>
        </div>

        {/* Prompts */}
        <h3 className="text-2xl font-bold text-gray-900 mb-6">The Prompts</h3>

        <PromptBlock
          title="Contact Form"
          description="A form that saves submissions and notifies you via email."
          prompt={`Add a contact form to my website:

Fields:
- Name (required)
- Email (required)
- Message (required)
- [OPTIONAL: Phone, Company, Budget dropdown]

When submitted:
- Save to database
- Send me an email notification at [YOUR EMAIL]
- Show success message to user

Spam protection: Add honeypot field or rate limiting`}
        />

        <PromptBlock
          title="Testimonials Section"
          description="Display client reviews and social proof."
          prompt={`Add a testimonials section to my website:

Display:
- Client/person name
- Their title/company (optional)
- Their photo (optional)
- The testimonial quote

Layout: [CHOOSE: Carousel / Grid / Stacked cards]

Make testimonials editable from admin panel.`}
        />

        <EmergentCTA />

        <PromptBlock
          title="Dark/Light Mode Toggle"
          description="Let users switch between light and dark themes."
          prompt={`Add a dark/light mode toggle to my website:

- Toggle button in header or footer
- Remember user's preference (localStorage)
- Smooth transition between modes
- Default to: [light / dark / system preference]`}
        />

        <PromptBlock
          title="Announcement Banner"
          description="A dismissible banner at the top of your site."
          prompt={`Add a dismissible announcement banner to the top of my website:

- Shows a short message across full width
- "X" button to dismiss
- Once dismissed, stays hidden for that session
- Editable from admin panel (text and link)
- Can be turned on/off from admin`}
        />

        <PromptBlock
          title="Booking Integration (Calendly)"
          description="Embed a booking calendar for calls."
          prompt={`Add a Calendly embed for booking calls with me:

- Embed on /book or /contact page
- Use my Calendly link: [YOUR CALENDLY URL]
- Match my website's styling
- Show available times in visitor's timezone
- Optional: Inline embed vs popup button`}
        />

        <PromptBlock
          title="FAQ Section"
          description="Expandable frequently asked questions."
          prompt={`Add an expandable FAQ section to my website:

Questions:
1. [QUESTION 1]
2. [QUESTION 2]
3. [QUESTION 3]
4. [QUESTION 4]

Style:
- Accordion style (click to expand/collapse)
- Smooth animation
- Only one question open at a time (optional)
- Match my site's design`}
        />

        <PromptBlock
          title="Pricing Table"
          description="Display your service tiers with a pricing table."
          prompt={`Add a pricing section with 3 tiers for my services:

Tier 1: [NAME]
- Price: [PRICE]
- Features: [LIST FEATURES]
- CTA: [BUTTON TEXT → LINK]

Tier 2: [NAME] (Featured/Recommended)
- Price: [PRICE]
- Features: [LIST FEATURES]
- CTA: [BUTTON TEXT → LINK]

Tier 3: [NAME]
- Price: [PRICE]
- Features: [LIST FEATURES]
- CTA: [BUTTON TEXT → LINK]

Style: [CHOOSE: Cards side by side / Highlight middle tier / Custom]`}
        />

        <AuthorCard author={resource.author} />
        
        <RelatedResources resourceIds={resource.relatedResources} />

        <AboutEmergent />
      </div>
    </div>
  );
};

export default AddOnFeaturesPage;
